import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import Icon from '../components/Icon';
import FunnelWaitVideo from '../components/FunnelWaitVideo';
import FunnelWelcomePage from './FunnelWelcomePage';
import FunnelBookStep, {
  CUSTOM_BOOK_KEY,
  FREE_BOOK_OPTIONS,
} from '../components/funnel/FunnelBookStep';
import { useFunnelAnalytics, FUNNEL_STARTED_KEY } from '../hooks/useFunnelAnalytics';
import { EVENTS } from '../constants/events';

// sessionStorage keys. The funnel's redirect_uri/state are NOT stored — they
// ride in the URL through the whole flow (incl. the Google round trip). Only
// the funnel's own answers persist here, so they can be saved to the account
// after sign-in.
const FUNNEL_STEP_KEY = 'rc_funnel_step';
const FUNNEL_NAME_KEY = 'rc_funnel_child_name';
const FUNNEL_CONCERN_KEY = 'rc_funnel_screen_concern';
const FUNNEL_PREVIEW_KEY = 'rc_funnel_preview_img';
// Book-promo variant only (?variant=book): the free storybook they picked.
// Held here for the same reason as the other answers — it has to survive the
// Google round trip, and it doesn't belong in a URL that ends up in analytics.
// The shipping address is NOT collected here; FunnelBookAddressPage asks for it
// later in the funnel, after checkout.
const FUNNEL_BOOK_KEY = 'rc_funnel_book_key';
const FUNNEL_BOOK_PROMPT_KEY = 'rc_funnel_book_prompt';
// Distinct from FUNNEL_STARTED_KEY so abandoning one funnel and entering the
// other in the same tab still counts as a start (same rule the intro funnel follows).
const BOOK_STARTED_KEY = 'rc_funnel_book_started';
// Written by AuthContext during a funnel sign-in only: whether the users row
// existed before that sign-in auto-created it (from the backend exchange)
const SIGNIN_USER_EXISTED_KEY = 'pixley_signin_user_existed';

const FUNNEL_KEYS = [
  FUNNEL_STEP_KEY, FUNNEL_NAME_KEY, FUNNEL_CONCERN_KEY,
  FUNNEL_PREVIEW_KEY, SIGNIN_USER_EXISTED_KEY,
  FUNNEL_BOOK_KEY, FUNNEL_BOOK_PROMPT_KEY, BOOK_STARTED_KEY,
  // Retired question — still cleared so a session that started on the old
  // bundle doesn't leave a stale answer behind in the tab.
  'rc_funnel_child_age',
  // Cleared too, so a second funnel run in the same tab counts as a new start
  FUNNEL_STARTED_KEY,
];
export const clearFunnelSession = () =>
  FUNNEL_KEYS.forEach((k) => sessionStorage.removeItem(k));

// iOS onboarding theme (NewOnboardingKit.swift)
const ACCENT = '#0A6CFF';
const CONFETTI_COLORS = [ACCENT, '#FFC83D', '#7C5CFF', '#34C759', '#FF6FA5'];

// Ready-made friends for visitors with no photo handy. `key` matches the
// backend's DEFAULT_AVATAR_KEYS (Pixley-Backend/routers/profiles_router.py) —
// the same starter characters new profiles get auto-assigned — so the funnel and
// the app offer one set, not two that drift apart.
//
// Served from /assets/funnel-friends/<key>.webp: 768px, 17-29KB each generated
// from the 1024px PNGs in /assets. 768 is exactly the cap blobToDataURL applies
// to a generated preview, so picking a friend and generating from a photo
// produce the same-sized character image downstream.
//
// `kind` is not rendered — it exists only for the aria-label, so a screen-reader
// user hears "Use Willow the deer" instead of a bare name for art they can't see.
const PRESET_FRIENDS = [
  { key: 'zippy-icon', name: 'Zippy', kind: 'tiger' },
  { key: 'benny', name: 'Benny', kind: 'bunny' },
  { key: 'willow', name: 'Willow', kind: 'deer' },
];

// ── variants ───────────────────────────────────────────────────────────────
// Two flows share this page. The default is the original web2app funnel; the
// book variant (?variant=book) inserts one step — pick a free storybook, say
// where to ship the printed copy — between meeting the character and signing in.
//
// A config rather than a fork: the flows differ by one step out of six, so
// forking would have meant a third copy of the photo/camera/generation
// machinery and ~800 lines of shared CSS, all free to drift apart.
export const BOOK_VARIANT = 'book';

const VARIANTS = {
  default: {
    stepOrder: ['name', 'concern', 'create', 'meet', 'signin'],
    // Full flow order (incl. transient steps) — used to pick the slide direction
    flowOrder: ['name', 'concern', 'create', 'generating', 'meet', 'signin', 'finishing'],
    // Where the header back button leads from each step
    backTarget: { concern: 'name', create: 'concern', signin: 'meet' },
    analytics: {},
  },
  [BOOK_VARIANT]: {
    stepOrder: ['name', 'concern', 'create', 'meet', 'book', 'signin'],
    flowOrder: ['name', 'concern', 'create', 'generating', 'meet', 'book', 'signin', 'finishing'],
    backTarget: { concern: 'name', create: 'concern', book: 'meet', signin: 'book' },
    // Its own funnel identity in PostHog. Both flows use the same step names in
    // a different order, so sharing one insight would give 'signin' two
    // different step_numbers and make either funnel's drop-off unreadable.
    analytics: {
      funnelName: 'web2app_book',
      viewOrder: ['welcome', 'name', 'concern', 'create', 'generating', 'meet', 'book', 'signin', 'finishing'],
      pathPrefix: '/funnel-book',
      startedKey: BOOK_STARTED_KEY,
    },
  },
};

// Screen-time struggles — mirrors the product's Goals/Safe Content voice
const CONCERN_OPTIONS = [
  { value: 'mindless-watching', icon: 'smartphone', label: 'Mindless watching', description: 'Endless videos with no real value' },
  { value: 'screen-battles', icon: 'sentiment_stressed', label: 'Screen-time battles', description: "Meltdowns when it's time to turn off" },
  { value: 'not-learning', icon: 'psychology', label: 'Not learning anything', description: "Entertainment that doesn't teach" },
  { value: 'unsafe-content', icon: 'shield', label: 'Unsafe content', description: 'Hard to trust what they stumble into' },
];

// The funnel is a sign-up flow: accounts that existed before this sign-in
// get welcomed back and sent to the main site instead.
//
// The authoritative answer comes from the backend: the OAuth exchange checks
// for the users row BEFORE auto-creating it and returns user_already_existed,
// which AuthContext stashes under SIGNIN_USER_EXISTED_KEY (funnel sign-ins
// only). The heuristic below only covers sessions where no exchange happened
// in this tab (e.g. the user was already signed in when they opened the
// funnel link — which itself means the account predates the funnel visit).
const ACCOUNT_AGE_NEW_MS = 15 * 60 * 1000;
// Exported so the second funnel (FunnelIntroAuthPage) makes the same call from
// the same signals — "is this a returning account?" decides who gets sent home
// instead of on to checkout, and two copies of it would drift.
export const isExistingAccount = (user) => {
  if (!user) return false;
  const existedFlag = sessionStorage.getItem(SIGNIN_USER_EXISTED_KEY);
  if (existedFlag === '1') return true;
  if (existedFlag === '0') return false;
  // Fallback inference: onboarded persona, any characters, or a row older
  // than this sign-in (fresh accounts are rows created seconds ago)
  let persona = user.persona;
  if (typeof persona === 'string') {
    try {
      persona = JSON.parse(persona);
    } catch {
      persona = null;
    }
  }
  if (persona && typeof persona === 'object' && !Array.isArray(persona) && Object.keys(persona).length > 0) return true;
  if (Array.isArray(user.characters) && user.characters.length > 0) return true;
  const joined = Date.parse(user.join_date || user.created_at || '');
  if (!Number.isNaN(joined) && Date.now() - joined > ACCOUNT_AGE_NEW_MS) return true;
  return false;
};

// How each struggle reads as a persona goal — same vocabulary the
// onboarding GoalsStep saves, so downstream personalization (mem0,
// story generation) treats funnel users like onboarded users.
const CONCERN_TO_GOAL = {
  'mindless-watching': 'Educational content',
  'screen-battles': 'Healthy screen-time habits',
  'not-learning': 'Learning',
  'unsafe-content': 'Safe content',
};

// Reassurance shown on the create step, tailored to the concern they picked
const CONCERN_REASSURANCE = {
  'mindless-watching': 'stories worth watching, starring',
  'screen-battles': 'screen time you can feel good about, starring',
  'not-learning': 'stories that spark imagination, starring',
  'unsafe-content': 'safe, kid-friendly stories, starring',
};

// Rotating status lines while the character generates
const GEN_MESSAGES = [
  'Pixley is sketching the outlines…',
  'Mixing the perfect colors…',
  'Adding a spark of magic…',
  'Putting on the finishing touches…',
];

// ── motion variants (step slide + staggered content reveal) ────────────────
const stepVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 56 : -56, scale: 0.98 }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir >= 0 ? -56 : 56,
    scale: 0.98,
    transition: { duration: 0.22, ease: 'easeIn' },
  }),
};

const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Ambient background — soft drifting color blobs + rising sparkle dots
const AMBIENT_DOTS = [
  { left: '6%', size: 6, delay: 0, dur: 16, color: 'rgba(10,108,255,0.35)' },
  { left: '16%', size: 4, delay: 5, dur: 21, color: 'rgba(255,200,61,0.45)' },
  { left: '28%', size: 5, delay: 9, dur: 18, color: 'rgba(124,92,255,0.35)' },
  { left: '44%', size: 4, delay: 2, dur: 23, color: 'rgba(10,108,255,0.30)' },
  { left: '58%', size: 6, delay: 12, dur: 19, color: 'rgba(255,111,165,0.35)' },
  { left: '70%', size: 4, delay: 6, dur: 17, color: 'rgba(10,108,255,0.40)' },
  { left: '82%', size: 5, delay: 10, dur: 22, color: 'rgba(255,200,61,0.40)' },
  { left: '92%', size: 4, delay: 3, dur: 20, color: 'rgba(124,92,255,0.40)' },
];

const AmbientBackground = () => (
  <div className="fa-ambient" aria-hidden="true">
    <span className="fa-blob fa-blob-1" />
    <span className="fa-blob fa-blob-2" />
    <span className="fa-blob fa-blob-3" />
    {AMBIENT_DOTS.map((d, i) => (
      <span
        key={i}
        className="fa-dot"
        style={{
          left: d.left,
          width: d.size,
          height: d.size,
          background: d.color,
          animationDelay: `${d.delay}s`,
          animationDuration: `${d.dur}s`,
        }}
      />
    ))}
  </div>
);

// ── image helpers ──────────────────────────────────────────────────────────

// Downscale a Blob to a JPEG data URL small enough for sessionStorage
const blobToDataURL = (blob, maxDim = 768) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

const dataURLtoBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// ── celebration bits (confetti + sparkles, from NOMeetPixleyStep) ──────────

const Confetti = () => {
  const pieces = useRef(
    Array.from({ length: 45 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 2.2 + Math.random() * 1.6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 360,
    }))
  ).current;
  return (
    <div className="fa-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

const SPARKLES = [
  { top: '-4%', left: '8%', size: 16, color: ACCENT },
  { top: '10%', left: '96%', size: 12, color: '#FFC83D' },
  { top: '46%', left: '-6%', size: 10, color: '#FFC83D' },
  { top: '68%', left: '102%', size: 14, color: ACCENT },
  { top: '96%', left: '14%', size: 11, color: ACCENT },
  { top: '88%', left: '84%', size: 13, color: '#FFC83D' },
];

const Sparkles = () => (
  <>
    {SPARKLES.map((s, i) => (
      <span
        key={i}
        className="fa-sparkle"
        style={{
          top: s.top,
          left: s.left,
          color: s.color,
          animationDelay: `${i * 0.18}s`,
        }}
        aria-hidden="true"
      >
        <Icon name="auto_awesome" size={s.size} fill />
      </span>
    ))}
  </>
);

/**
 * Authentication step for RevenueCat Web Funnels, rebuilt as the iOS
 * onboarding (new_onboarding/): name your hero → add a photo → watch Pixley
 * bring them to life → sign in to save them → redirect back to the funnel
 * with `state` + `app_user_id`.
 * https://www.revenuecat.com/docs/tools/funnels/creating-funnels#authentication-steps
 */
const FunnelAuthPage = ({ variant = 'default' }) => {
  const {
    stepOrder: STEP_ORDER,
    flowOrder: FLOW_ORDER,
    backTarget: BACK_TARGET,
    analytics: analyticsConfig,
  } = VARIANTS[variant] || VARIANTS.default;
  const isBookVariant = variant === BOOK_VARIANT;

  const [searchParams] = useSearchParams();
  const { signInWithGoogle, isAuthenticated, loading, user } = useAuth();
  const { apiClient } = useAPI();
  const hasCompletedRef = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [step, setStepState] = useState(() => {
    const saved = sessionStorage.getItem(FUNNEL_STEP_KEY);
    // 'generating' can't be resumed after a reload — restart from create
    if (saved === 'generating') return 'create';
    return saved && STEP_ORDER.includes(saved) ? saved : 'name';
  });
  const [childName, setChildName] = useState(() => sessionStorage.getItem(FUNNEL_NAME_KEY) || '');
  const [screenConcern, setScreenConcern] = useState(() => sessionStorage.getItem(FUNNEL_CONCERN_KEY) || '');
  const [previewImage, setPreviewImage] = useState(() => sessionStorage.getItem(FUNNEL_PREVIEW_KEY) || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  // Key of the ready-made friend currently being loaded (null = none)
  const [loadingFriend, setLoadingFriend] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [genMsgIndex, setGenMsgIndex] = useState(0);
  const [showExistingAccount, setShowExistingAccount] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem(FUNNEL_STEP_KEY));

  // Book variant only. Restored from sessionStorage so a reload — or the Google
  // round trip, which reloads this page — doesn't lose the choice.
  const [bookKey, setBookKey] = useState(() => sessionStorage.getItem(FUNNEL_BOOK_KEY) || '');
  const [customPrompt, setCustomPrompt] = useState(() => sessionStorage.getItem(FUNNEL_BOOK_PROMPT_KEY) || '');

  // Every funnel event carries the answers collected so far, so drop-off can be
  // sliced by screen-time concern without joining to the account.
  const funnel = useFunnelAnalytics(
    {
      screen_concern: screenConcern || null,
      ...(isBookVariant ? { free_book_key: bookKey || null } : {}),
    },
    analyticsConfig
  );

  // Slide direction: moving forward slides left, going back slides right
  const prevStepRef = useRef(step);
  const direction = FLOW_ORDER.indexOf(step) >= FLOW_ORDER.indexOf(prevStepRef.current) ? 1 : -1;
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  const setStep = useCallback((next) => {
    sessionStorage.setItem(FUNNEL_STEP_KEY, next);
    setStepState(next);
    window.scrollTo({ top: 0 });
  }, []);

  // Tap an option → show the selected state for a beat, then auto-advance
  const advanceTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(advanceTimerRef.current), []);

  // Existing-account modal lingers a moment before leaving for the main site
  const redirectTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  // Buffer the generating-step panda video while they pick a photo —
  // otherwise it only starts downloading when the wait screen mounts, which
  // on a slow connection means a blank circle at the funnel's most anxious
  // moment. The detached element is held in a ref so buffering isn't GC'd;
  // the wait screen's <video> then plays from the media cache instantly.
  const prefetchVideoRef = useRef(null);
  useEffect(() => {
    if (step !== 'create' || prefetchVideoRef.current) return;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    [
      { src: '/assets/loading-panda.webm', type: 'video/webm' },
      { src: '/assets/loading-panda.mp4', type: 'video/mp4' },
    ].forEach(({ src, type }) => {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      video.appendChild(source);
    });
    video.load();
    prefetchVideoRef.current = video;
  }, [step]);

  // Warm the API connection (DNS + TCP + TLS) the moment a photo is chosen —
  // generation is one tap away, and a real request beats a preconnect hint
  // that would have idled out minutes before the user got here.
  const apiWarmedRef = useRef(false);
  useEffect(() => {
    if (!photoFile || apiWarmedRef.current || !apiClient) return;
    apiWarmedRef.current = true;
    apiClient.healthCheck().catch(() => {});
  }, [photoFile, apiClient]);

  // The name is never sent to analytics — only its length, so a truncated or
  // placeholder entry is still visible in the drop-off data.
  const submitName = (method) => {
    funnel.trackStepCompleted('name', {
      method,
      name_length: childName.trim().length,
    });
    setStep('concern');
  };

  const selectConcern = (value) => {
    setScreenConcern(value);
    sessionStorage.setItem(FUNNEL_CONCERN_KEY, value);
    funnel.trackStepCompleted('concern', { screen_concern: value });
    clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => setStep('create'), 350);
  };

  // ── book variant: the free-storybook step ────────────────────────────────
  // Every answer is mirrored into sessionStorage as it changes rather than on
  // submit, so a mid-step reload (or a phone switching apps to autofill an
  // address) doesn't send them back to an empty form.
  const selectBook = (key) => {
    setBookKey(key);
    sessionStorage.setItem(FUNNEL_BOOK_KEY, key);
  };

  const changeCustomPrompt = (value) => {
    setCustomPrompt(value);
    sessionStorage.setItem(FUNNEL_BOOK_PROMPT_KEY, value);
  };

  const submitBook = () => {
    funnel.trackStepCompleted('book', {
      book_key: bookKey,
      is_custom: bookKey === CUSTOM_BOOK_KEY,
    });
    setStep('signin');
  };

  // Funnel params. On first arrival RevenueCat passes `redirect_uri`/`state`;
  // after the Google round trip we land back here with the same values under
  // `funnel_redirect_uri`/`funnel_state` (prefixed so they don't collide with
  // the OAuth `state`/`code` Supabase appends). These stay in the URL the whole
  // time, so they're never cached — only the funnel's own answers (name,
  // concern, character preview) live in sessionStorage.
  const redirectUri =
    searchParams.get('redirect_uri') || searchParams.get('funnel_redirect_uri');
  const funnelState =
    searchParams.get('state') || searchParams.get('funnel_state');

  // ── analytics: one step_viewed + virtual pageview per view ────────────────
  // Every step of this funnel lives at /funnel-auth, so nothing distinguishes
  // them in PostHog unless each one announces itself here.
  useEffect(() => {
    if (!redirectUri || !funnelState) {
      funnel.trackInvalidLink();
      return;
    }
    funnel.trackView(showWelcome ? 'welcome' : step);
  }, [showWelcome, step, redirectUri, funnelState, funnel]);

  // ── completion: save the held character, then bounce back to the funnel ──
  const completeAndRedirect = useCallback(async () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Hand the authenticated user back to the RevenueCat funnel (→ checkout /
    // next step) carrying the unchanged state + their app_user_id.
    const returnToFunnel = async ({ isExisting }) => {
      try {
        const result = await apiClient.completeFunnelAuth(redirectUri, funnelState);
        if (result.status && result.data?.redirect_url) {
          logger.log('✅ Funnel auth complete, returning to funnel');
          // Fire before clearFunnelSession() — it wipes the answers this event
          // reports — and send it unbatched, since the redirect below can
          // outrun PostHog's next flush.
          funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
            outcome: 'funnel_checkout',
            is_existing_account: isExisting,
            has_character: !!sessionStorage.getItem(FUNNEL_PREVIEW_KEY),
          });
          clearFunnelSession();
          setShowSuccess(true);
          window.location.replace(result.data.redirect_url);
        } else {
          throw new Error(result.error || 'Failed to complete funnel authentication');
        }
      } catch (error) {
        logger.error('❌ Funnel auth completion error:', error);
        funnel.track(EVENTS.FUNNEL_COMPLETION_FAILED, {
          is_existing_account: isExisting,
          error_message: error.message || 'unknown',
          status_code: error.status || null,
        });
        hasCompletedRef.current = false;
        setIsSigningIn(false);
        setStep('signin');
        showToast(error.message || 'Something went wrong. Please try again.', 'error');
      }
    };

    // The funnel is for NEW sign-ups. An existing account keeps all its data
    // untouched (no character/persona writes). Where it goes next depends on
    // whether it already has an active RevenueCat subscription: only paying
    // subscribers skip the funnel and get welcomed home — everyone else is
    // sent on to the funnel checkout to (re)subscribe.
    if (isExistingAccount(user)) {
      let isSubscribed = false;
      try {
        // force=true busts the backend resolve cache for a fresh RevenueCat
        // read (they may have subscribed/lapsed since it was last cached).
        const sub = await apiClient.getSubscriptionStatus(user.id, { force: true });
        const tier = sub?.data?.subscription;
        isSubscribed = Boolean(sub?.data?.premium) || (!!tier && tier !== 'NONE');
      } catch (error) {
        // On an RC/backend hiccup, don't shortcut them home on an unverified
        // subscription — let them continue through the funnel checkout.
        logger.warn('Funnel: subscription check failed for returning account:', error);
      }

      funnel.track(EVENTS.FUNNEL_EXISTING_ACCOUNT, { is_subscribed: isSubscribed });

      if (isSubscribed) {
        logger.log('👋 Existing subscriber — skipping funnel, redirecting home');
        funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
          outcome: 'existing_subscriber_home',
          is_existing_account: true,
          has_character: !!sessionStorage.getItem(FUNNEL_PREVIEW_KEY),
        });
        clearFunnelSession();
        setShowExistingAccount(true);
        redirectTimerRef.current = setTimeout(() => {
          window.location.replace('https://www.pixleyai.com');
        }, 3400);
        return;
      }

      logger.log('↩️ Existing account without an active subscription — continuing to funnel checkout');
      setStep('finishing');
      await returnToFunnel({ isExisting: true });
      return;
    }

    setStep('finishing');

    // Best-effort: keep the character they just made (mirrors the iOS flow,
    // which uploads the held preview after sign-in). Never blocks the funnel.
    // The id is held onto because the book variant's free storybook has to
    // star this character, and there's nowhere else to get it.
    let savedCharacterId = null;
    try {
      const previewDataUrl = sessionStorage.getItem(FUNNEL_PREVIEW_KEY);
      const savedName = sessionStorage.getItem(FUNNEL_NAME_KEY);
      if (previewDataUrl && savedName && user?.id) {
        const saveResult = await apiClient.saveFunnelCharacter({
          userId: user.id,
          name: savedName,
          animatedImage: dataURLtoBlob(previewDataUrl),
        });
        savedCharacterId = saveResult?.character_id || saveResult?.data?.character_id || null;
        logger.log('✅ Funnel character saved to account');
      }
    } catch (error) {
      logger.warn('Funnel character save failed (non-blocking):', error);
    }

    // Best-effort: seed the account persona with everything the funnel
    // learned (child's name, screen-time struggle) in the same shape
    // OnboardingContext saves, so stories personalize from day one —
    // and since a non-empty persona marks onboarding as done, funnel users
    // aren't re-onboarded in-app. Existing personas are merged, not replaced.
    try {
      const personaName = sessionStorage.getItem(FUNNEL_NAME_KEY);
      const personaConcern = sessionStorage.getItem(FUNNEL_CONCERN_KEY);
      if ((personaName || personaConcern) && user?.id) {
        let existing = user.persona;
        if (typeof existing === 'string') {
          try {
            existing = JSON.parse(existing);
          } catch {
            existing = null;
          }
        }
        if (!existing || typeof existing !== 'object' || Array.isArray(existing)) existing = {};

        const concernGoal = CONCERN_TO_GOAL[personaConcern];
        const goals = [...new Set([...(existing.goals || []), ...(concernGoal ? [concernGoal] : [])])];

        const persona = {
          favorite_shows: [],
          aggregated_topics: [],
          aggregated_interests: [],
          aggregated_preferences: [],
          profile_picture: null,
          discovery_source: [],
          ...existing,
          child_name: existing.child_name || personaName || null,
          // The funnel no longer asks for an age, but the key is still written
          // so an existing value survives the merge rather than being dropped
          // from the stored persona.
          child_age: existing.child_age || null,
          screen_time_concern: existing.screen_time_concern || personaConcern || null,
          goals,
          onboarding_source: existing.onboarding_source || 'web_funnel',
        };

        await apiClient.updateUserPersona(user.id, persona);
        logger.log('✅ Funnel persona saved to account');
      }
    } catch (error) {
      logger.warn('Funnel persona save failed (non-blocking):', error);
    }

    // Book variant: claim the free storybook so it's already generating by the
    // time they open the app. Awaited rather than fire-and-forget because
    // returnToFunnel() below calls location.replace(), which aborts anything
    // still in flight — and the backend returns as soon as the row exists, so
    // this costs a round trip, not a generation wait.
    //
    // Requires the character: the storybook stars it, and if the save above
    // failed there is nothing to build the book around.
    if (isBookVariant && savedCharacterId && user?.id) {
      const chosenBook = sessionStorage.getItem(FUNNEL_BOOK_KEY);
      try {
        if (chosenBook) {
          const claim = await apiClient.claimFunnelFreeBook({
            userId: user.id,
            characterId: savedCharacterId,
            bookKey: chosenBook,
            customPrompt: sessionStorage.getItem(FUNNEL_BOOK_PROMPT_KEY) || '',
            childName: sessionStorage.getItem(FUNNEL_NAME_KEY) || '',
          });
          funnel.track(EVENTS.FUNNEL_FREE_BOOK_CLAIMED, {
            book_key: chosenBook,
            is_custom: chosenBook === CUSTOM_BOOK_KEY,
            print_order_status: claim?.data?.print_order_status || null,
          });
          logger.log('✅ Funnel free book claimed');
        }
      } catch (error) {
        // Never hold the sign-up hostage to the gift. The claim is idempotent
        // per account, so the app can retry it later without double-printing.
        logger.warn('Funnel free book claim failed (non-blocking):', error);
        funnel.track(EVENTS.FUNNEL_FREE_BOOK_FAILED, {
          book_key: chosenBook || null,
          error_message: error.message || 'unknown',
          status_code: error.status || null,
        });
      }
    }

    // Stamp the person so funnel sign-ups stay identifiable in cohorts long
    // after the funnel events themselves scroll out of the retention window.
    funnel.setPersonProperties({
      signup_source: 'web_funnel',
      funnel_name: isBookVariant ? 'web2app_book' : 'web2app',
      screen_time_concern: sessionStorage.getItem(FUNNEL_CONCERN_KEY) || null,
      ...(isBookVariant ? { claimed_free_book: true } : {}),
    });

    await returnToFunnel({ isExisting: false });
  }, [apiClient, redirectUri, funnelState, user, setStep, funnel, isBookVariant]);

  // Fires when the user lands back authenticated after the OAuth round trip
  // (step === 'signin'), or arrives already signed in before starting
  // (step === 'name' untouched) — either way, finish and return to the funnel.
  useEffect(() => {
    if (loading || !isAuthenticated || !apiClient) return;
    if (hasCompletedRef.current) return;
    if (!redirectUri || !funnelState) {
      logger.warn('Funnel auth: missing redirect_uri or state');
      return;
    }
    // Funnel data is waiting to be saved (character and/or persona) — hold
    // until the user object (and its id) has loaded so the saves aren't
    // silently skipped
    const hasFunnelData =
      sessionStorage.getItem(FUNNEL_PREVIEW_KEY) ||
      sessionStorage.getItem(FUNNEL_NAME_KEY) ||
      sessionStorage.getItem(FUNNEL_CONCERN_KEY) ||
      sessionStorage.getItem(FUNNEL_BOOK_KEY);
    if (hasFunnelData && !user?.id) return;
    if (step === 'signin' || step === 'name') {
      completeAndRedirect();
    }
  }, [isAuthenticated, loading, apiClient, redirectUri, funnelState, step, user, completeAndRedirect]);

  // ── generation (same /previewCharacterImage the iOS onboarding calls) ────
  useEffect(() => {
    if (step !== 'generating') return undefined;
    // Ease toward 95%, slowing as it approaches — keeps the bar alive during
    // an open-ended wait without ever finishing early (NewOnboardingFlow)
    const id = setInterval(() => {
      setGenProgress((p) => (p >= 0.95 ? p : p + (0.95 - p) * 0.08));
    }, 500);
    return () => clearInterval(id);
  }, [step]);

  // Cycle the "Pixley is painting" status lines while generating
  useEffect(() => {
    if (step !== 'generating') return undefined;
    setGenMsgIndex(0);
    const id = setInterval(() => setGenMsgIndex((i) => (i + 1) % GEN_MESSAGES.length), 2600);
    return () => clearInterval(id);
  }, [step]);

  const startGeneration = async (image) => {
    setGenProgress(0);
    setStep('generating');

    const startedAt = Date.now();
    // Counts the /previewCharacterImage attempts this run actually spent, so a
    // slow-but-successful retry is distinguishable from a first-try success.
    let attempts = 0;
    funnel.track(EVENTS.FUNNEL_GENERATION_STARTED, {
      photo_size_kb: image?.size ? Math.round(image.size / 1024) : null,
    });

    try {
      let tokenResp;
      try {
        tokenResp = await apiClient.getFunnelPreviewToken(redirectUri, funnelState);
      } catch (error) {
        if (error.status === 429) {
          // Minting is IP rate-limited as a flood backstop; the generation
          // budget itself is charged later, on /previewCharacterImage.
          throw new Error("You've hit the character-preview limit for now. Please wait a bit and try again.");
        }
        throw error;
      }
      const previewToken = tokenResp.token || tokenResp.data?.token;
      if (!previewToken) throw new Error('Could not start character generation. Please try again.');

      // 2 auto-attempts max. Each one charges the per-IP budget
      // (WEB_PREVIEW_IP_MAX_PER_HOUR, 8/hour) as well as the per-session one,
      // so keep this small — it must leave room for manual "try a different
      // photo" do-overs before the visitor hits a 429.
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        attempts += 1;
        try {
          const blob = await apiClient.previewCharacterImageWeb({
            name: childName,
            image,
            handdrawn: false,
            previewToken,
          });
          const dataUrl = await blobToDataURL(blob, 768);
          sessionStorage.setItem(FUNNEL_PREVIEW_KEY, dataUrl);
          setPreviewImage(dataUrl);
          setGenProgress(1);
          setHeroRevealed(false);
          funnel.track(EVENTS.FUNNEL_GENERATION_COMPLETED, {
            duration_seconds: Math.round((Date.now() - startedAt) / 1000),
            attempts,
          });
          funnel.trackStepCompleted('create', { creation_method: 'photo' });
          setTimeout(() => setStep('meet'), 400);
          return;
        } catch (error) {
          lastError = error;
          // Only retry faults that might be transient. A 4xx is deterministic for
          // this input — safety block, blocked name, oversize photo, or a spent
          // preview budget (429) — so retrying returns the same answer AND spends
          // another slot from the per-IP budget. 5xx can be a blip, so retry those.
          if (error.status >= 400 && error.status < 500) break;
          if (/inappropriate|safety|copyright/i.test(error.message || '')) break;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      throw lastError || new Error('Character generation failed');
    } catch (error) {
      logger.error('❌ Funnel preview generation failed:', error);
      funnel.track(EVENTS.FUNNEL_GENERATION_FAILED, {
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        attempts,
        error_message: error.message || 'unknown',
        status_code: error.status || null,
        // Safety blocks and rate limits are the two failures worth splitting
        // out: one is content, the other is capacity.
        is_rate_limited: error.status === 429,
        is_safety_block: /inappropriate|safety|copyright/i.test(error.message || ''),
      });
      showToast(error.message || "We couldn't bring your character to life. Please try again.", 'error');
      setStep('create');
    }
  };

  const handleGenerate = () => {
    if (!photoFile) {
      showToast('Add a photo first!', 'warning');
      return;
    }
    startGeneration(photoFile);
  };

  // Picking a ready-made friend skips image generation entirely: no
  // /previewCharacterImage call, so it costs nothing, never charges the per-IP
  // preview budget, and can't fail on a safety block. The chosen art is written
  // into FUNNEL_PREVIEW_KEY in exactly the shape a generated preview takes
  // (768px JPEG data URL), so the meet step, sign-in and saveFunnelCharacter
  // downstream need no special-casing — the character is still saved under the
  // child's name, the friend is just the artwork standing in for a photo.
  const choosePresetFriend = async (friend) => {
    if (loadingFriend) return;
    setLoadingFriend(friend.key);
    try {
      const response = await fetch(`/assets/funnel-friends/${friend.key}.webp`);
      if (!response.ok) throw new Error(`Could not load ${friend.name}`);
      const dataUrl = await blobToDataURL(await response.blob(), 768);
      sessionStorage.setItem(FUNNEL_PREVIEW_KEY, dataUrl);
      setPreviewImage(dataUrl);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        friend_name: friend.name,
        success: true,
        // Whether the escape hatch is used instead of a photo, or after one
        // was already added, says different things about the photo ask.
        had_photo: !!photoFile,
      });
      funnel.trackStepCompleted('create', { creation_method: 'preset_friend' });
      // No progress bar to run down — straight to the reveal
      setHeroRevealed(false);
      setStep('meet');
    } catch (error) {
      logger.error('Preset friend selection failed:', error);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        success: false,
        error_message: error.message || 'unknown',
      });
      showToast("We couldn't load that friend. Please try another.", 'error');
    } finally {
      setLoadingFriend(null);
    }
  };

  // `source` distinguishes the two hidden inputs ('upload' = gallery/file
  // picker, 'camera_native' = the mobile capture input) for analytics; the
  // desktop in-page camera reports itself from capturePhoto().
  const handlePhotoChange = (e, source = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
      method: source,
      // A replaced photo usually means the first attempt was rejected or
      // disliked — worth separating from a first add.
      is_replacement: !!photoFile,
      photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
      photo_type: file.type || null,
    });
  };

  // ── "Take photo" ──────────────────────────────────────────────────────────
  // The input's `capture` attribute only opens a camera on phones/tablets —
  // desktop browsers silently ignore it and show the file picker. On desktop
  // we open an in-page getUserMedia camera instead.
  const isTouchDevice =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)); // iPadOS reports as Mac

  const openCamera = async () => {
    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
    } catch (error) {
      logger.warn('Camera unavailable, falling back to file picker:', error);
      showToast("We couldn't open your camera — choose a photo instead.", 'warning');
      fileInputRef.current?.click();
    }
  };

  const closeCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    setCameraOpen(false);
  }, []);

  // Attach the stream once the modal's <video> is in the DOM
  useEffect(() => {
    if (cameraOpen && cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  // Never leave the camera running on unmount
  useEffect(() => closeCamera, [closeCamera]);

  const capturePhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setPhotoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
        funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
          method: 'camera_web',
          is_replacement: !!photoFile,
          photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
          photo_type: file.type || null,
        });
        closeCamera();
      },
      'image/jpeg',
      0.92
    );
  };

  // Hero pop-in on the meet step (spring-ish scale, then confetti)
  useEffect(() => {
    if (step !== 'meet') return undefined;
    const id = setTimeout(() => setHeroRevealed(true), 120);
    return () => clearTimeout(id);
  }, [step]);

  const handleGoogleSignIn = async () => {
    try {
      logger.log('Starting Google OAuth (funnel)...');
      setIsSigningIn(true);
      // Fired before the redirect: this is the last event PostHog gets from
      // this page load, so signin_started vs. the post-OAuth 'finishing' view
      // is what measures Google round-trip abandonment.
      funnel.trackStepCompleted('signin', { method: 'google' }, { immediate: true });
      funnel.trackImmediate(EVENTS.FUNNEL_SIGNIN_STARTED, { method: 'google' });

      // redirect_uri/state ride back in the return URL below — no need to
      // stash them. Only the funnel's own answers stay in sessionStorage so
      // they can be saved to the account after the Google round trip.
      sessionStorage.setItem(FUNNEL_STEP_KEY, 'signin');
      if (childName) sessionStorage.setItem(FUNNEL_NAME_KEY, childName);

      // Land straight back on /funnel-auth (not the site root) after Google,
      // carrying the funnel params in the URL so the flow resumes without
      // depending on any storage. Prefixed names avoid colliding with the
      // OAuth `state`/`code` that Supabase appends to this URL on return.
      const returnUrl = new URL('/funnel-auth', window.location.origin);
      // Without this the OAuth return resolves to the default funnel and the
      // book answers held in sessionStorage are never claimed.
      if (variant !== 'default') returnUrl.searchParams.set('variant', variant);
      if (redirectUri) returnUrl.searchParams.set('funnel_redirect_uri', redirectUri);
      if (funnelState) returnUrl.searchParams.set('funnel_state', funnelState);

      const timeoutId = setTimeout(() => {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, { method: 'google', reason: 'timeout' });
        showToast('Sign in is taking too long. Please try again.', 'error');
      }, 15000);

      const result = await signInWithGoogle(returnUrl.toString());
      clearTimeout(timeoutId);

      if (result && result.success) {
        logger.log('Google OAuth initiated successfully');
      } else {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
          method: 'google',
          reason: 'oauth_init_failed',
          error_message: result?.error || 'unknown',
        });
        showToast(result?.error || 'Sign in failed. Please try again.', 'error');
      }
    } catch (error) {
      logger.error('Google sign in error:', error);
      setIsSigningIn(false);
      funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
        method: 'google',
        reason: 'exception',
        error_message: error.message || 'unknown',
      });
      showToast(error.message || 'Failed to sign in with Google', 'error');
    }
  };

  const canGenerate = !!photoFile;

  // How the picked book reads in prose. A custom story has no title yet — it
  // gets one when the pipeline writes it — so it's referred to generically.
  const bookSelectionTitle =
    bookKey === CUSTOM_BOOK_KEY
      ? 'their story'
      : `“${FREE_BOOK_OPTIONS.find((o) => o.key === bookKey)?.title || 'their storybook'}”`;

  const stepIndex = STEP_ORDER.indexOf(step === 'generating' ? 'create' : step === 'finishing' ? 'signin' : step);

  const invalidLink = !redirectUri || !funnelState;

  // ── render ────────────────────────────────────────────────────────────────
  const cardKey = invalidLink ? 'invalid' : step;

  if (showWelcome && !invalidLink) {
    return (
      <FunnelWelcomePage
        onContinue={() => {
          funnel.trackStepCompleted('welcome');
          setStep('name');
          setShowWelcome(false);
        }}
      />
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <FunnelAuthPageStyles />
      <div className="fa-bg">
        <AmbientBackground />
        <div className="fa-shell">

          {/* Step header (segmented progress, like the iOS flow) */}
          {!invalidLink && step !== 'finishing' && (
            <motion.div
              className="fa-header"
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="fa-back-slot">
                <AnimatePresence>
                  {BACK_TARGET[step] && (
                    <motion.button
                      className="fa-back"
                      onClick={() => {
                        funnel.track(EVENTS.FUNNEL_BACK_CLICKED, {
                          from_step: step,
                          to_step: BACK_TARGET[step],
                        });
                        setStep(BACK_TARGET[step]);
                      }}
                      aria-label="Back"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
              <div className="fa-segments">
                {STEP_ORDER.map((s, i) => (
                  <div key={s} className={`fa-segment ${i <= stepIndex ? 'filled' : ''}`} />
                ))}
              </div>
              <div className="fa-step-count">Step {Math.max(1, stepIndex + 1)} of {STEP_ORDER.length}</div>
            </motion.div>
          )}

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={cardKey}
              className={`fa-card fa-center ${cardKey === 'meet' ? 'fa-meet' : ''} ${cardKey === 'book' ? 'fa-book-card' : ''}`}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {invalidLink && (
                <>
                  <motion.div variants={itemVariants} className="fa-panda-wrap">
                    <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Hmm, this link isn't quite right</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    This page is part of the Pixley sign-up flow. Head to{' '}
                    <a href="https://www.pixleyai.com">pixleyai.com</a> to get started.
                  </motion.p>
                </>
              )}

              {/* STEP 1 — name (NOChildNameStep) */}
              {!invalidLink && step === 'name' && (
                <>
                  <motion.div variants={itemVariants} className="fa-panda-wrap">
                    <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.div variants={itemVariants} className="fa-eyebrow">Let's get started</motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">What's your child's name?</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    They're about to become the hero of their very own cartoons.
                  </motion.p>
                  <motion.div variants={itemVariants}>
                    <input
                      className="fa-input"
                      type="text"
                      value={childName}
                      maxLength={24}
                      placeholder="Their name"
                      onChange={(e) => {
                        setChildName(e.target.value);
                        sessionStorage.setItem(FUNNEL_NAME_KEY, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && childName.trim().length >= 2) submitName('enter_key');
                      }}
                    />
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <button
                      className="fa-primary-btn"
                      disabled={childName.trim().length < 2}
                      onClick={() => submitName('button')}
                    >
                      Continue <span className="fa-chevron">›</span>
                    </button>
                  </motion.div>
                </>
              )}

              {/* STEP 2 — screen-time struggle */}
              {!invalidLink && step === 'concern' && (
                <>
                  <motion.div variants={itemVariants} className="fa-eyebrow">One quick question</motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">
                    What's your biggest struggle with screen time?
                  </motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    Pixley turns screen time into stories that are actually good for {childName.trim()}.
                  </motion.p>
                  <motion.div variants={itemVariants} className="fa-concern-list">
                    {CONCERN_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        className={`fa-concern-option ${screenConcern === opt.value ? 'selected' : ''}`}
                        onClick={() => selectConcern(opt.value)}
                      >
                        <span className="fa-concern-icon"><Icon name={opt.icon} size={24} /></span>
                        <span className="fa-concern-text">
                          <strong>{opt.label}</strong>
                          <small>{opt.description}</small>
                        </span>
                        <span className="fa-concern-check" aria-hidden="true"><Icon name="check" size={15} /></span>
                      </button>
                    ))}
                  </motion.div>
                </>
              )}

              {/* STEP 4 — add a photo (NOCreatePixleyStep) */}
              {!invalidLink && step === 'create' && (
                <>
                  <motion.div variants={itemVariants} className="fa-panda-wrap">
                    <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.div variants={itemVariants} className="fa-eyebrow">Create their new friend</motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Help us bring {childName.trim()} to life</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    {CONCERN_REASSURANCE[screenConcern]
                      ? `Upload a photo and we'll create ${CONCERN_REASSURANCE[screenConcern]} ${childName.trim()}.`
                      : `Upload a photo and we'll turn ${childName.trim()} into a cartoon hero.`}
                  </motion.p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handlePhotoChange(e, 'camera_native')}
                    style={{ display: 'none' }}
                  />

                  {photoFile ? (
                    <motion.div variants={itemVariants}>
                      <button className="fa-photo-card" onClick={() => fileInputRef.current?.click()}>
                        <img src={photoPreviewUrl} alt="Selected" className="fa-photo-thumb" />
                        <span className="fa-photo-text">
                          <strong>Photo added</strong>
                          <small>Tap to change</small>
                        </span>
                        <span className="fa-photo-check"><Icon name="check" size={16} /></span>
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div variants={itemVariants} className="fa-photo-options">
                      <button className="fa-photo-option" onClick={() => fileInputRef.current?.click()}>
                        <span className="fa-photo-option-icon"><Icon name="photo_library" size={30} /></span>
                        <strong>Upload photo</strong>
                        <small>Choose from gallery</small>
                      </button>
                      <button className="fa-photo-option" onClick={openCamera}>
                        <span className="fa-photo-option-icon"><Icon name="photo_camera" size={30} /></span>
                        <strong>Take photo</strong>
                        <small>Use your camera</small>
                      </button>
                    </motion.div>
                  )}

                  <motion.div variants={itemVariants}>
                    <button className="fa-primary-btn" disabled={!canGenerate} onClick={handleGenerate}>
                      Generate {childName.trim()} <span className="fa-chevron">›</span>
                    </button>
                  </motion.div>

                  {/* Escape hatch: no photo handy → start with a ready-made
                      friend. Deliberately below the primary CTA so the photo
                      path stays the obvious one. */}
                  <motion.div variants={itemVariants} className="fa-friends">
                    <div className="fa-friends-divider">
                      <span>or pick a ready-made friend</span>
                    </div>
                    <div className="fa-friends-row">
                      {PRESET_FRIENDS.map((friend) => (
                        <button
                          key={friend.key}
                          className={`fa-friend${loadingFriend === friend.key ? ' is-loading' : ''}`}
                          onClick={() => choosePresetFriend(friend)}
                          disabled={!!loadingFriend}
                          aria-label={`Use ${friend.name} the ${friend.kind}`}
                        >
                          <span className="fa-friend-art">
                            <img
                              src={`/assets/funnel-friends/${friend.key}.webp`}
                              alt=""
                              loading="lazy"
                              width="72"
                              height="72"
                              // Fall back to the full-size PNG the rest of the
                              // app uses if WebP 404s or fails to decode
                              onError={(e) => {
                                e.target.src = `/assets/${friend.key}.png`;
                              }}
                            />
                            {loadingFriend === friend.key && <span className="fa-friend-spinner" />}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}

              {/* STEP 2b — generating (NOCharacterGenerationStep) */}
              {!invalidLink && step === 'generating' && (
                <>
                  <motion.div variants={itemVariants}>
                    <div className="fa-panda-glow">
                      <video className="fa-panda-video" autoPlay muted loop playsInline>
                        <source src="/assets/loading-panda.webm" type="video/webm" />
                        <source src="/assets/loading-panda.mp4" type="video/mp4" />
                      </video>
                    </div>
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Bringing {childName.trim()} to life…</motion.h1>
                  <motion.div variants={itemVariants} className="fa-gen-msg">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={genMsgIndex}
                        className="fa-subtitle"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {GEN_MESSAGES[genMsgIndex]}
                      </motion.p>
                    </AnimatePresence>
                  </motion.div>
                  <motion.div variants={itemVariants} className="fa-progress-row">
                    <div className="fa-progress-track">
                      <div className="fa-progress-fill" style={{ width: `${Math.round(genProgress * 100)}%` }}>
                        <div className="fa-progress-shimmer" />
                      </div>
                    </div>
                    <span className="fa-progress-label">{Math.round(genProgress * 100)}%</span>
                  </motion.div>

                  {/* The one open-ended wait in the funnel — offer something to
                      watch rather than just the bar. Plays in an overlay, so the
                      generation request in flight here is never abandoned. */}
                  <FunnelWaitVideo
                    onPlay={(episode) =>
                      funnel.track(EVENTS.FUNNEL_WAIT_VIDEO_PLAYED, {
                        episode_id: episode?.id || null,
                        episode_name: episode?.name || null,
                        // Where in the wait they gave up on the bar
                        progress_percent: Math.round(genProgress * 100),
                      })
                    }
                  />
                </>
              )}

              {/* STEP 3 — meet the character (NOMeetPixleyStep) */}
              {!invalidLink && step === 'meet' && previewImage && (
                <>
                  {heroRevealed && <Confetti />}
                  <motion.div variants={itemVariants} className="fa-eyebrow">Meet your new friend</motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">This is {childName.trim()}!</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    The hero of every cartoon and bedtime story, made just for them.
                  </motion.p>
                  <div className={`fa-hero-frame ${heroRevealed ? 'revealed' : ''}`}>
                    <span className="fa-hero-halo" aria-hidden="true" />
                    <Sparkles />
                    <img src={previewImage} alt={childName.trim()} className="fa-hero-img" />
                  </div>
                  <motion.div variants={itemVariants}>
                    <button
                      className="fa-primary-btn"
                      onClick={() => {
                        funnel.trackStepCompleted('meet');
                        setStep(isBookVariant ? 'book' : 'signin');
                      }}
                    >
                      Save {childName.trim()}'s character <span className="fa-chevron">›</span>
                    </button>
                  </motion.div>
                </>
              )}

              {/* STEP 5 (book variant only) — pick the free storybook */}
              {!invalidLink && step === 'book' && (
                <FunnelBookStep
                  childName={childName}
                  previewImage={previewImage}
                  bookKey={bookKey}
                  customPrompt={customPrompt}
                  onSelectBook={selectBook}
                  onCustomPromptChange={changeCustomPrompt}
                  onSubmit={submitBook}
                  onTrack={(event, props) => funnel.track(event, props)}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 4 — sign in (NOSignInStep) */}
              {!invalidLink && step === 'signin' && (
                <>
                  <motion.div variants={itemVariants} className="fa-eyebrow">Last step</motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">
                    {isBookVariant
                      ? 'Create your account to claim it'
                      : childName.trim() ? `Save ${childName.trim()}'s character` : 'Welcome to Pixley!'}
                  </motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    {isBookVariant
                      ? `We'll start writing ${bookSelectionTitle} right away — it'll be waiting when you open the app.`
                      : 'Create your account to keep this character and every story you make.'}
                  </motion.p>

                  {previewImage ? (
                    <motion.div variants={itemVariants} className="fa-signin-avatar-wrap">
                      <div className="fa-signin-avatar">
                        <img src={previewImage} alt={childName.trim()} />
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div variants={itemVariants} className="fa-panda-wrap">
                      <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                    </motion.div>
                  )}

                  {isSigningIn ? (
                    <div className="fa-signing-in">
                      <div className="fa-spinner" />
                      <span>Setting up your account…</span>
                    </div>
                  ) : (
                    <motion.div variants={itemVariants}>
                      <button className="fa-google-btn" onClick={handleGoogleSignIn}>
                        <svg className="fa-google-icon" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </button>
                    </motion.div>
                  )}
                </>
              )}

              {/* Finishing — saving character + returning to the funnel */}
              {!invalidLink && step === 'finishing' && (
                showSuccess ? (
                  <>
                    <div className="fa-success-check"><Icon name="check" size={42} /></div>
                    <h1 className="fa-title">You're all set!</h1>
                    <p className="fa-subtitle">Taking you back to finish signing up…</p>
                  </>
                ) : (
                  <>
                    <motion.div variants={itemVariants}>
                      <div className="fa-panda-glow">
                        <video className="fa-panda-video" autoPlay muted loop playsInline>
                          <source src="/assets/loading-panda.webm" type="video/webm" />
                          <source src="/assets/loading-panda.mp4" type="video/mp4" />
                        </video>
                      </div>
                    </motion.div>
                    <motion.h1 variants={itemVariants} className="fa-title">Setting up your account…</motion.h1>
                    <motion.p variants={itemVariants} className="fa-subtitle">
                      {childName.trim() ? `Saving ${childName.trim()}'s character to your account.` : 'Just a moment.'}
                    </motion.p>
                  </>
                )
              )}
            </motion.div>
          </AnimatePresence>

          <motion.p
            className="fa-footer-note"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            PIXLEY
          </motion.p>

        </div>

        {/* In-page camera (desktop, where the capture attribute is ignored) */}
        <AnimatePresence>
          {cameraOpen && (
            <motion.div
              className="fa-camera-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className="fa-camera-modal"
                initial={{ opacity: 0, scale: 0.92, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              >
                <video ref={cameraVideoRef} className="fa-camera-video" autoPlay playsInline muted />
                <div className="fa-camera-actions">
                  <button className="fa-camera-cancel" onClick={closeCamera}>Cancel</button>
                  <button className="fa-camera-shutter" onClick={capturePhoto} aria-label="Take photo" />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Existing account — welcome back, then off to the main site */}
        <AnimatePresence>
          {showExistingAccount && (
            <motion.div
              className="fa-exist-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="fa-exist-modal"
                role="dialog"
                aria-modal="true"
                aria-label="You already have an account"
                initial={{ opacity: 0, scale: 0.82, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <span className="fa-exist-glow" aria-hidden="true" />
                <motion.img
                  className="fa-panda"
                  src="/assets/pixley-panda-nobg.png"
                  alt="Pixley Panda"
                  initial={{ scale: 0, rotate: -14 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 15, delay: 0.15 }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="fa-eyebrow">Welcome back</div>
                  <h1 className="fa-title">You already have an account!</h1>
                  <p className="fa-subtitle">No need to sign up again — we're taking you back to Pixley.</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <div className="fa-exist-progress" aria-hidden="true"><div /></div>
                  <button
                    className="fa-primary-btn"
                    onClick={() => window.location.replace('https://www.pixleyai.com')}
                  >
                    Take me there now <span className="fa-chevron">›</span>
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

// iOS onboarding theme, translated from NewOnboardingKit.swift:
// bg #F5FAFF→#E8F1FF, ink #0B1A38, subtitle #6C7A8F, accent #0A6CFF,
// card stroke #E8EDF5, track #DDE6F2, rounded Fredoka titles.
const FunnelAuthPageStyles = () => (
  <style>{`
    .fa-bg {
      position: relative;
      min-height: 100vh;
      min-height: 100dvh;
      background:
        radial-gradient(1100px 750px at 85% -10%, rgba(124,92,255,0.10), transparent 60%),
        radial-gradient(950px 700px at -10% 110%, rgba(10,108,255,0.10), transparent 60%),
        linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(32px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(32px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }

    /* Touch polish — no gray tap flash, no double-tap zoom delay */
    .fa-primary-btn, .fa-google-btn, .fa-back, .fa-photo-option, .fa-photo-card,
    .fa-camera-shutter, .fa-camera-cancel, .fa-input {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    /* Ambient animated background (blobs drift, sparkle dots rise) */
    .fa-ambient {
      position: fixed; inset: 0;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
    }
    .fa-blob { position: absolute; border-radius: 50%; filter: blur(70px); }
    .fa-blob-1 {
      width: 500px; height: 500px; top: -180px; left: -140px;
      background: rgba(10,108,255,0.14);
      animation: faDrift1 24s ease-in-out infinite;
    }
    .fa-blob-2 {
      width: 440px; height: 440px; bottom: -160px; right: -120px;
      background: rgba(124,92,255,0.12);
      animation: faDrift2 28s ease-in-out infinite;
    }
    .fa-blob-3 {
      width: 380px; height: 380px; top: 30%; right: -100px;
      background: rgba(255,200,61,0.12);
      animation: faDrift3 32s ease-in-out infinite;
    }
    @keyframes faDrift1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(70px, 50px) scale(1.12); }
    }
    @keyframes faDrift2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-60px, -70px) scale(1.08); }
    }
    @keyframes faDrift3 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-80px, 40px) scale(1.15); }
    }
    .fa-dot {
      position: absolute; bottom: -12px;
      border-radius: 50%;
      opacity: 0;
      animation: faDotRise linear infinite;
    }
    @keyframes faDotRise {
      0% { transform: translateY(0) scale(1); opacity: 0; }
      8% { opacity: 0.7; }
      85% { opacity: 0.5; }
      100% { transform: translateY(-105vh) scale(0.6); opacity: 0; }
    }

    /* Shell centers itself in the viewport; margin:auto degrades to scroll
       gracefully when the content is taller than the screen */
    .fa-shell {
      width: 100%; max-width: 560px;
      margin: auto;
      position: relative; z-index: 1;
    }

    /* Step header */
    .fa-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin: 0 4px 22px;
    }
    .fa-back-slot {
      width: 40px; height: 40px; flex-shrink: 0;
      transition: width 0.35s cubic-bezier(.22,1,.36,1);
    }
    .fa-back {
      width: 40px; height: 40px;
      display: flex; align-items: center; justify-content: center;
      background: #FFF;
      border: 1px solid #E8EDF5;
      border-radius: 14px;
      color: #0B1A38;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(11,26,51,0.06);
      transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
    }
    .fa-back:hover {
      border-color: rgba(10,108,255,0.35);
      box-shadow: 0 4px 14px rgba(10,108,255,0.15);
      color: #0A6CFF;
    }
    .fa-segments { display: flex; gap: 6px; flex: 1; }
    .fa-segment {
      height: 6px; flex: 1; border-radius: 3px;
      background: #DDE6F2;
      overflow: hidden;
    }
    .fa-segment::after {
      content: '';
      display: block;
      height: 100%; width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #4D94FF);
      transition: width 0.6s cubic-bezier(.22,1,.36,1);
    }
    .fa-segment.filled::after { width: 100%; }
    .fa-step-count {
      font-size: 13px; font-weight: 600; color: #6C7A8F; white-space: nowrap;
    }

    /* Card */
    .fa-card {
      position: relative;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(232,237,245,0.9);
      border-radius: 28px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.10), 0 4px 14px rgba(11,26,51,0.05);
      padding: 40px 32px 36px;
      overflow: hidden;
    }
    .fa-center { text-align: center; }

    .fa-footer-note {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 700;
      color: #8A97AB;
      letter-spacing: 3px;
      margin: 18px 0 0;
    }

    /* Type */
    .fa-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 2px; text-transform: uppercase;
      color: #0A6CFF; margin-bottom: 10px;
    }
    .fa-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 31px; font-weight: 700;
      color: #0B1A38; line-height: 1.15;
      letter-spacing: -0.3px;
      margin: 0 0 10px;
    }
    .fa-subtitle {
      font-size: 16px; color: #6C7A8F; line-height: 1.55;
      margin: 0 0 24px;
    }
    .fa-subtitle a { color: #0A6CFF; font-weight: 600; }

    /* Panda mascot */
    .fa-panda-wrap { display: flex; justify-content: center; }
    .fa-panda {
      width: 118px; height: 118px;
      object-fit: contain;
      margin-bottom: 10px;
      filter: drop-shadow(0 10px 18px rgba(11,26,51,0.14));
    }
    .fa-float { animation: faFloat 2.6s ease-in-out infinite; }
    @keyframes faFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }
    .fa-panda-glow {
      width: 150px; height: 150px; margin: 0 auto 12px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.16) 0%, rgba(10,108,255,0) 70%);
      display: flex; align-items: center; justify-content: center;
      animation: faPulse 1.9s ease-in-out infinite;
    }
    @keyframes faPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .fa-panda-video { width: 140px; height: 140px; object-fit: contain; }

    /* Inputs */
    .fa-input {
      width: 100%;
      padding: 16px 18px;
      font-size: 18px; font-weight: 600;
      font-family: 'Fredoka', 'Inter', sans-serif;
      color: #0B1A38;
      text-align: center;
      background: #F7FAFF;
      border: 2px solid #E8EDF5;
      border-radius: 16px;
      outline: none;
      margin-bottom: 20px;
      box-sizing: border-box;
      transition: border-color 0.2s, box-shadow 0.25s, background 0.2s;
    }
    .fa-input:focus {
      border-color: #0A6CFF;
      background: #FFF;
      box-shadow: 0 0 0 4px rgba(10,108,255,0.13);
    }
    .fa-input::placeholder { color: #A9B6C9; font-weight: 500; }

    /* Buttons */
    .fa-primary-btn {
      position: relative;
      width: 100%;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 17px 22px;
      background: linear-gradient(135deg, #0A6CFF 0%, #3D8BFF 60%, #5EA1FF 100%);
      color: #FFF;
      font-size: 18px; font-weight: 700;
      font-family: 'Fredoka', 'Inter', sans-serif;
      border: none; border-radius: 18px;
      cursor: pointer;
      overflow: hidden;
      box-shadow: 0 8px 20px rgba(10,108,255,0.30), inset 0 1px 0 rgba(255,255,255,0.25);
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, opacity 0.2s;
    }
    .fa-primary-btn::before {
      content: '';
      position: absolute; top: 0; left: -80%;
      width: 60%; height: 100%;
      background: linear-gradient(105deg, transparent, rgba(255,255,255,0.35), transparent);
      transform: skewX(-20deg);
      transition: left 0.6s ease;
    }
    .fa-primary-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px rgba(10,108,255,0.40), inset 0 1px 0 rgba(255,255,255,0.25);
    }
    .fa-primary-btn:hover:not(:disabled)::before { left: 130%; }
    .fa-primary-btn:active:not(:disabled) { transform: scale(0.97); }
    .fa-primary-btn:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
    .fa-chevron { font-size: 20px; line-height: 1; transition: transform 0.2s ease; }
    .fa-primary-btn:hover:not(:disabled) .fa-chevron { transform: translateX(3px); }

    .fa-google-btn {
      width: 100%;
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 15px 24px;
      background: #FFF;
      border: 1px solid #E8EDF5;
      border-radius: 18px;
      font-size: 16px; font-weight: 600;
      font-family: 'Inter', sans-serif;
      color: #0B1A38;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(11,26,51,0.06);
      transition: transform 150ms cubic-bezier(.2,.8,.2,1), border-color 0.2s, box-shadow 0.2s;
    }
    .fa-google-btn:hover {
      transform: translateY(-2px);
      border-color: rgba(10,108,255,0.30);
      box-shadow: 0 8px 20px rgba(10,108,255,0.14);
    }
    .fa-google-btn:active { transform: scale(0.98); }
    .fa-google-icon { width: 22px; height: 22px; }

    .fa-primary-btn:focus-visible, .fa-google-btn:focus-visible,
    .fa-back:focus-visible, .fa-photo-option:focus-visible,
    .fa-photo-card:focus-visible {
      outline: 3px solid rgba(10,108,255,0.4);
      outline-offset: 2px;
    }

    /* Photo options (Upload / Take photo, like NOCreatePixleyStep) */
    .fa-photo-options {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 20px;
    }
    .fa-photo-option {
      display: flex; flex-direction: column; align-items: center; gap: 4px;
      padding: 24px 12px;
      background: #F7FAFF;
      border: 2px dashed #C9D8EE;
      border-radius: 18px;
      cursor: pointer;
      color: #0B1A38;
      font-family: 'Inter', sans-serif;
      transition: border-color 0.2s, transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s, box-shadow 0.2s;
    }
    .fa-photo-option:hover {
      border-color: #0A6CFF;
      background: #FFF;
      transform: translateY(-3px);
      box-shadow: 0 10px 22px rgba(10,108,255,0.12);
    }
    .fa-photo-option:active { transform: translateY(-1px) scale(0.98); }
    .fa-photo-option-icon {
      margin-bottom: 4px;
      color: #0A6CFF;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.25s cubic-bezier(.34,1.5,.5,1);
    }
    .fa-photo-option:hover .fa-photo-option-icon { transform: scale(1.18) rotate(-5deg); }
    .fa-photo-option strong { font-size: 15px; }
    .fa-photo-option small { font-size: 12px; color: #6C7A8F; }

    /* Ready-made friends (secondary path when there's no photo to hand) */
    .fa-friends { margin-top: 22px; }
    .fa-friends-divider {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 14px;
      font-family: 'Inter', sans-serif;
      font-size: 12.5px; font-weight: 600;
      color: #8494AB;
      letter-spacing: 0.01em;
    }
    .fa-friends-divider::before, .fa-friends-divider::after {
      content: ''; flex: 1; height: 1px;
      background: linear-gradient(90deg, transparent, #D9E4F3, transparent);
    }
    /* Art only, no captions — centered cluster rather than a stretched grid,
       which left three lone circles marooned across the full card width */
    .fa-friends-row {
      display: flex;
      justify-content: center;
      gap: 14px;
    }
    .fa-friend {
      display: flex; align-items: center; justify-content: center;
      padding: 5px;
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: #0B1A38;
      font-family: 'Inter', sans-serif;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s;
    }
    .fa-friend:hover:not(:disabled) { background: #F7FAFF; transform: translateY(-3px); }
    .fa-friend:active:not(:disabled) { transform: translateY(-1px) scale(0.97); }
    /* While one friend loads, only the others dim */
    .fa-friend:disabled { cursor: default; opacity: 0.5; }
    .fa-friend.is-loading { opacity: 1; }
    .fa-friend-art {
      position: relative;
      width: 92px; height: 92px;
      border-radius: 50%;
      overflow: hidden;
      background: #F1F6FF;
      box-shadow: 0 0 0 2px #E3ECF9, 0 4px 10px rgba(11,26,51,0.08);
      transition: box-shadow 0.2s;
    }
    .fa-friend:hover:not(:disabled) .fa-friend-art {
      box-shadow: 0 0 0 2px #0A6CFF, 0 8px 18px rgba(10,108,255,0.18);
    }
    .fa-friend-art img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fa-friend-spinner {
      position: absolute; inset: 0;
      background: rgba(255,255,255,0.72);
      display: block;
    }
    .fa-friend-spinner::after {
      content: '';
      position: absolute; top: 50%; left: 50%;
      width: 20px; height: 20px; margin: -10px 0 0 -10px;
      border: 2.5px solid rgba(10,108,255,0.25);
      border-top-color: #0A6CFF;
      border-radius: 50%;
      animation: fa-friend-spin 0.7s linear infinite;
    }
    @keyframes fa-friend-spin { to { transform: rotate(360deg); } }

    /* Screen-time concern picker (stacked rows) */
    .fa-concern-list { display: flex; flex-direction: column; gap: 10px; }
    .fa-concern-option {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 16px;
      background: #F7FAFF;
      border: 2px solid #E8EDF5;
      border-radius: 16px;
      cursor: pointer;
      text-align: left;
      font-family: 'Inter', sans-serif;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: border-color 0.2s, background 0.2s, transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.2s;
    }
    .fa-concern-option:hover {
      border-color: rgba(10,108,255,0.45);
      background: #FFF;
      transform: translateY(-2px);
      box-shadow: 0 8px 18px rgba(10,108,255,0.10);
    }
    .fa-concern-option:active { transform: scale(0.98); }
    .fa-concern-option.selected {
      border-color: #0A6CFF;
      background: #F0F6FF;
      box-shadow: 0 8px 20px rgba(10,108,255,0.16);
    }
    .fa-concern-icon {
      width: 42px; height: 42px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      color: #0A6CFF;
      background: #FFF;
      border: 1px solid #E8EDF5;
      border-radius: 12px;
    }
    .fa-concern-option.selected .fa-concern-icon { border-color: rgba(10,108,255,0.35); }
    .fa-concern-text { flex: 1; display: flex; flex-direction: column; gap: 1px; }
    .fa-concern-text strong { font-size: 15px; color: #0B1A38; }
    .fa-concern-text small { font-size: 12.5px; color: #6C7A8F; line-height: 1.35; }
    .fa-concern-check {
      width: 22px; height: 22px; flex-shrink: 0;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700;
      color: #FFF;
      background: #0A6CFF;
      opacity: 0;
      transform: scale(0.5);
      transition: opacity 0.2s, transform 0.25s cubic-bezier(.34,1.5,.5,1);
    }
    .fa-concern-option.selected .fa-concern-check { opacity: 1; transform: scale(1); }

    .fa-photo-card {
      width: 100%;
      display: flex; align-items: center; gap: 14px;
      padding: 12px 16px;
      margin-bottom: 20px;
      background: #FFF;
      border: 2px solid #0A6CFF;
      border-radius: 16px;
      cursor: pointer;
      text-align: left;
      box-shadow: 0 4px 14px rgba(10,108,255,0.10);
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.2s;
    }
    .fa-photo-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(10,108,255,0.16);
    }
    .fa-photo-thumb {
      width: 52px; height: 52px;
      border-radius: 12px;
      object-fit: cover;
    }
    .fa-photo-text { flex: 1; display: flex; flex-direction: column; }
    .fa-photo-text strong { font-size: 16px; color: #0B1A38; }
    .fa-photo-text small { font-size: 13px; color: #6C7A8F; }
    .fa-photo-check {
      width: 24px; height: 24px;
      background: #34C759;
      color: #FFF;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700;
      animation: faCheckPop 0.45s cubic-bezier(.34,1.5,.5,1);
    }
    @keyframes faCheckPop {
      from { transform: scale(0); }
      to { transform: scale(1); }
    }

    /* In-page camera modal (desktop fallback for the capture attribute) */
    .fa-camera-overlay {
      position: fixed; inset: 0; z-index: 60;
      background: rgba(11,26,51,0.72);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-camera-modal {
      width: min(560px, 100%);
      background: #FFF;
      border-radius: 24px;
      padding: 16px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.35);
    }
    .fa-camera-video {
      width: 100%;
      border-radius: 16px;
      transform: scaleX(-1); /* mirror the live preview like a selfie cam */
      background: #0B1A38;
    }
    .fa-camera-actions {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      padding-top: 14px;
    }
    .fa-camera-cancel {
      position: absolute; left: 8px;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 600; color: #6C7A8F;
      cursor: pointer;
      transition: color 0.2s;
    }
    .fa-camera-cancel:hover { color: #0B1A38; }
    .fa-camera-shutter {
      width: 58px; height: 58px;
      border-radius: 50%;
      background: #0A6CFF;
      border: 4px solid #FFF;
      box-shadow: 0 0 0 3px #0A6CFF;
      cursor: pointer;
      transition: transform 0.15s cubic-bezier(.2,.8,.2,1);
    }
    .fa-camera-shutter:hover { transform: scale(1.08); }
    .fa-camera-shutter:active { transform: scale(0.92); }

    /* Existing-account modal */
    .fa-exist-overlay {
      position: fixed; inset: 0; z-index: 70;
      background: rgba(11,26,51,0.55);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-exist-modal {
      position: relative;
      width: min(430px, 100%);
      background: #FFF;
      border-radius: 28px;
      padding: 36px 28px 28px;
      text-align: center;
      overflow: hidden;
      box-shadow: 0 30px 80px rgba(11,26,51,0.35);
    }
    .fa-exist-glow {
      position: absolute; top: -70px; left: 50%;
      width: 260px; height: 260px;
      transform: translateX(-50%);
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.14) 0%, rgba(10,108,255,0) 70%);
      pointer-events: none;
    }
    .fa-exist-modal .fa-panda { display: block; margin: 0 auto 10px; }
    .fa-exist-modal .fa-title { font-size: 26px; }
    .fa-exist-modal .fa-subtitle { margin-bottom: 20px; }
    .fa-exist-progress {
      height: 6px;
      background: #E8EDF5;
      border-radius: 3px;
      overflow: hidden;
      margin: 0 0 18px;
    }
    .fa-exist-progress div {
      height: 100%;
      width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #7C5CFF);
      animation: faExistCountdown 3.4s linear forwards;
    }
    @keyframes faExistCountdown { to { width: 100%; } }

    /* Progress bar (NOProgressBar with shimmer) */
    .fa-gen-msg {
      min-height: 50px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 18px;
    }
    .fa-gen-msg .fa-subtitle { margin: 0; }
    .fa-progress-row { display: flex; align-items: center; gap: 12px; }
    .fa-progress-track {
      flex: 1; height: 12px;
      background: #DDE6F2;
      border-radius: 6px;
      overflow: hidden;
    }
    .fa-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #0A6CFF, #4D94FF);
      border-radius: 6px;
      position: relative;
      overflow: hidden;
      transition: width 0.5s ease-in-out;
    }
    .fa-progress-shimmer {
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
      animation: faShimmer 1.4s linear infinite;
    }
    @keyframes faShimmer {
      from { transform: translateX(-100%); }
      to { transform: translateX(100%); }
    }
    .fa-progress-label {
      font-size: 15px; font-weight: 700; color: #0B1A38;
      font-variant-numeric: tabular-nums;
      min-width: 44px; text-align: right;
    }

    /* Meet step — hero reveal */
    .fa-meet { overflow: visible; }
    .fa-hero-frame {
      position: relative;
      width: 340px; height: 340px;
      margin: 8px auto 30px;
      border-radius: 40px;
      border: 5px solid #FFF;
      box-shadow: 0 16px 44px rgba(11,26,51,0.18);
      overflow: visible;
      transform: scale(0);
      opacity: 0;
    }
    .fa-hero-frame.revealed { animation: faHeroPop 0.7s cubic-bezier(0.34, 1.45, 0.5, 1) forwards; }
    @keyframes faHeroPop {
      0% { transform: scale(0); opacity: 0; }
      70% { transform: scale(1.06); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    .fa-hero-halo {
      position: absolute; inset: -24px;
      border-radius: 50%;
      background: conic-gradient(from 0deg,
        rgba(10,108,255,0.30), rgba(124,92,255,0.22), rgba(255,200,61,0.26),
        rgba(255,111,165,0.22), rgba(10,108,255,0.30));
      filter: blur(24px);
      animation: faHaloSpin 9s linear infinite;
      z-index: 0;
    }
    @keyframes faHaloSpin { to { transform: rotate(360deg); } }
    .fa-hero-img {
      position: relative; z-index: 1;
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 35px;
      display: block;
    }
    .fa-sparkle {
      position: absolute;
      animation: faTwinkle 1.1s ease-in-out infinite alternate;
      pointer-events: none;
      z-index: 2;
    }
    @keyframes faTwinkle {
      from { transform: scale(0.7); opacity: 0.4; }
      to { transform: scale(1.2); opacity: 1; }
    }

    /* Confetti */
    .fa-confetti {
      position: absolute; inset: 0;
      pointer-events: none;
      overflow: hidden;
      border-radius: 28px;
      z-index: 1;
    }
    .fa-confetti span {
      position: absolute;
      top: -20px;
      width: 11px; height: 11px;
      border-radius: 2px;
      animation-name: faConfettiFall;
      animation-timing-function: ease-in;
      animation-fill-mode: forwards;
    }
    @keyframes faConfettiFall {
      to {
        transform: translate(var(--drift), 640px) rotate(var(--spin));
        opacity: 0;
      }
    }

    /* Sign-in step avatar */
    .fa-signin-avatar-wrap { display: flex; justify-content: center; }
    .fa-signin-avatar {
      width: 230px; height: 230px;
      margin: 4px auto 26px;
      border-radius: 50%;
      padding: 5px;
      background: linear-gradient(135deg, #0A6CFF, #7C5CFF, #FFC83D);
      box-sizing: border-box;
      animation: faAvatarGlow 3s ease-in-out infinite;
    }
    .fa-signin-avatar img {
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 50%;
      border: 4px solid #FFF;
      box-sizing: border-box;
      display: block;
    }
    @keyframes faAvatarGlow {
      0%, 100% { box-shadow: 0 12px 32px rgba(10,108,255,0.28); }
      50% { box-shadow: 0 14px 42px rgba(124,92,255,0.38); }
    }

    .fa-signing-in {
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 15px;
      font-size: 16px; font-weight: 600; color: #0B1A38;
    }
    .fa-spinner {
      width: 22px; height: 22px;
      border: 3px solid #DDE6F2;
      border-top-color: #0A6CFF;
      border-radius: 50%;
      animation: faSpin 0.8s linear infinite;
    }
    @keyframes faSpin { to { transform: rotate(360deg); } }

    /* Success */
    .fa-success-check {
      position: relative;
      width: 80px; height: 80px;
      margin: 0 auto 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3EDC8F, #20A96A);
      color: #FFF;
      font-size: 34px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 26px rgba(32,169,106,0.35);
      animation: faSuccessPop 0.6s ease-out;
    }
    .fa-success-check::after {
      content: '';
      position: absolute; inset: 0;
      border-radius: 50%;
      border: 3px solid rgba(62,220,143,0.5);
      animation: faRipple 1.4s ease-out infinite;
    }
    @keyframes faSuccessPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes faRipple {
      from { transform: scale(1); opacity: 1; }
      to { transform: scale(1.7); opacity: 0; }
    }

    /* Mobile */
    @media (max-width: 480px) {
      .fa-bg {
        padding: calc(20px + env(safe-area-inset-top, 0px))
                 calc(12px + env(safe-area-inset-right, 0px))
                 calc(20px + env(safe-area-inset-bottom, 0px))
                 calc(12px + env(safe-area-inset-left, 0px));
      }
      .fa-card { padding: 30px 20px 28px; border-radius: 24px; }
      .fa-confetti { border-radius: 24px; }
      .fa-title { font-size: 26px; }
      .fa-subtitle { font-size: 15px; }
      .fa-hero-frame { width: 290px; height: 290px; }
      .fa-signin-avatar { width: 200px; height: 200px; }
      .fa-step-count { display: none; }
      .fa-panda { width: 100px; height: 100px; }

      /* Comfortable 44px thumb target; the empty slot collapses so the
         progress bar is centered on steps without a back button */
      .fa-header { gap: 12px; margin-bottom: 18px; }
      .fa-back-slot { width: 44px; height: 44px; }
      .fa-back { width: 44px; height: 44px; }
      .fa-back-slot:empty { width: 0; }

      /* Cheaper compositing on phone GPUs */
      .fa-blob { filter: blur(50px); }
      .fa-blob-3 { display: none; }

      .fa-photo-option { padding: 20px 10px; }
      .fa-friends { margin-top: 18px; }
      .fa-friend-art { width: 84px; height: 84px; }
      .fa-primary-btn { padding: 16px 20px; font-size: 17px; }
      .fa-google-btn { padding: 14px 20px; font-size: 15px; }
      .fa-footer-note { font-size: 12px; margin-top: 14px; }
    }

    /* Small phones (iPhone SE, compact Androids) */
    @media (max-width: 374px) {
      .fa-card { padding: 26px 16px 24px; }
      .fa-title { font-size: 23px; }
      .fa-panda { width: 88px; height: 88px; }
      .fa-hero-frame { width: 250px; height: 250px; }
      .fa-signin-avatar { width: 175px; height: 175px; }
      .fa-photo-options { gap: 10px; }
      .fa-photo-option-icon { font-size: 26px; }
      .fa-photo-option strong { font-size: 14px; }
      .fa-friends-row { gap: 10px; }
      .fa-friend-art { width: 74px; height: 74px; }
      .fa-friends-divider { font-size: 11.5px; }
      .fa-concern-option { padding: 12px 12px; gap: 11px; }
      .fa-concern-icon { width: 38px; height: 38px; font-size: 20px; }
    }

    /* 641-700px tall — iPhone SE and friends. Too short for the enlarged
       character reveal at its phone size, but not short enough to trigger the
       640px rule below, so the reveal alone gets an intermediate step down.
       Bounded at both ends rather than written as a plain max-height so it
       can't fight that rule on genuinely short screens, whatever the order. */
    @media (min-height: 641px) and (max-height: 700px) {
      .fa-hero-frame { width: 235px; height: 235px; }
      .fa-signin-avatar { width: 168px; height: 168px; }
    }

    /* Short viewports (landscape phones, small screens with keyboard up):
       compress the tall decorative pieces so the card fits without scroll */
    @media (max-height: 640px) {
      .fa-bg { padding-top: 14px; padding-bottom: 14px; }
      .fa-header { margin-bottom: 12px; }
      .fa-card { padding: 24px 22px 22px; }
      .fa-title { font-size: 24px; }
      .fa-subtitle { margin-bottom: 16px; }
      .fa-panda { width: 78px; height: 78px; margin-bottom: 6px; }
      .fa-panda-glow { width: 112px; height: 112px; margin-bottom: 8px; }
      .fa-panda-video { width: 104px; height: 104px; }
      .fa-hero-frame { width: 210px; height: 210px; margin-bottom: 18px; }
      .fa-signin-avatar { width: 150px; height: 150px; margin-bottom: 18px; }
      .fa-gen-msg { min-height: 42px; margin-bottom: 14px; }
      .fa-footer-note { display: none; }
      .fa-concern-option { padding: 11px 14px; }
    }

    /* Touch devices — bigger camera shutter for thumbs */
    @media (pointer: coarse) {
      .fa-camera-shutter { width: 66px; height: 66px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fa-float, .fa-panda-glow, .fa-progress-shimmer, .fa-sparkle,
      .fa-blob, .fa-dot, .fa-hero-halo, .fa-signin-avatar,
      .fa-photo-check, .fa-success-check, .fa-success-check::after { animation: none; }
      /* Keep the loading spinner — it's feedback, not decoration — but drop the
         hover lift that reduced-motion users don't want */
      .fa-friend:hover:not(:disabled) { transform: none; }
      .fa-hero-frame.revealed { animation: none; transform: scale(1); opacity: 1; }
      .fa-confetti { display: none; }
      .fa-primary-btn::before { display: none; }
    }
  `}</style>
);

export default FunnelAuthPage;
